import { remindersService } from "@/services/reminders.service";
import { FilterOperation, FilterSettingDto, FilterType } from "@/types/filter";
import {
  BaseReminders,
  CreateReminder,
  ReminderType,
  UpdateReminder,
} from "@/types/reminders";
import { useCallback, useState } from "react";
import { PAGE_SIZE } from "@/utils/constants";

export const useReminders = (memberId: string) => {
  const [reminders, setReminders] = useState<BaseReminders[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<ReminderType | null>(null);
  const [hasReminders, setHasReminders] = useState<boolean | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const fetchReminders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: FilterSettingDto = {
        filters: {},
        sort: [],
        pagination: { page: 1, pageSize: PAGE_SIZE },
      };

      if (searchTerm) {
        filters.filters = {
          reminderFor: {
            operation: FilterOperation.CONTAINS,
            value: searchTerm,
            type: FilterType.STRING,
          },
        };
      }

      if (category && category !== ReminderType.ALL) {
        filters.filters = {
          type: {
            operation: FilterOperation.EQUAL,
            value: category,
            type: FilterType.STRING,
          },
        };
      }
      const response = await remindersService.getReminders(memberId, filters);
      setReminders(response.data);
      setPage(1);
      setHasMore(response.data.length === PAGE_SIZE);
      setTotalCount(response.total || 0);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setError(
        err instanceof Error ? err.message : "Failed to fetch reminders"
      );
    }
  }, [memberId, category, searchTerm]);

  const getReminder = useCallback(
    async (reminderId: string) => {
      setLoading(true);
      setError(null);
      try {
        const response = await remindersService.getReminder(
          memberId,
          reminderId
        );
        return response;
      } catch (err) {
        setLoading(false);
        setError(
          err instanceof Error ? err.message : "Failed to fetch reminder"
        );
      }
    },
    [memberId]
  );

  const addReminder = async (reminder: CreateReminder) => {
    setLoading(true);
    setError(null);
    try {
      const response = await remindersService.createReminder(
        memberId,
        reminder
      );
      setReminders([...reminders, response]);
      setLoading(false);
      return response;
    } catch (err) {
      setLoading(false);
      const errorMessage =
        err.response?.data?.message ||
        (err instanceof Error ? err.message : "Failed to add reminder");
      setError(errorMessage);
      throw err; // Re-throw the error so it can be caught by the component
    }
  };

  const updateReminder = async (
    reminderId: string,
    reminder: UpdateReminder
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await remindersService.updateReminder(
        memberId,
        reminderId,
        reminder
      );
      setReminders((prevReminders) =>
        prevReminders.map((r) => (r.id === reminder.id ? response : r))
      );
      return response;
    } catch (err) {
      setLoading(false);
      setError(
        err instanceof Error ? err.message : "Failed to update reminder"
      );
    }
  };

  const deleteReminder = async (reminderId: string) => {
    setLoading(true);
    setError(null);
    try {
      await remindersService.deleteReminder(memberId, reminderId);
      setReminders(reminders.filter((r) => r.id !== reminderId));
    } catch (err) {
      setLoading(false);
      setError(
        err instanceof Error ? err.message : "Failed to delete reminder"
      );
    }
  };

  const checkRemindersPresent = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await remindersService.hasReminders(memberId);
      setHasReminders(response);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setError(
        err instanceof Error ? err.message : "Failed to fetch reminders"
      );
    }
  }, [memberId]);

  const fetchMoreReminders = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    setError(null);
    try {
      const nextPage = page + 1;
      const filters: FilterSettingDto = {
        filters: {},
        sort: [],
        pagination: { page: nextPage, pageSize: PAGE_SIZE },
      };

      if (searchTerm) {
        filters.filters = {
          reminderFor: {
            operation: FilterOperation.CONTAINS,
            value: searchTerm,
            type: FilterType.STRING,
          },
        };
      }

      if (category && category !== ReminderType.ALL) {
        filters.filters = {
          type: {
            operation: FilterOperation.EQUAL,
            value: category,
            type: FilterType.STRING,
          },
        };
      }

      const response = await remindersService.getReminders(memberId, filters);
      const newReminders = response.data || [];

      setReminders((prev) => [...prev, ...newReminders]);
      setPage(nextPage);
      setHasMore(newReminders.length === PAGE_SIZE);
      if (response.total !== undefined) {
        setTotalCount(response.total);
      }
      setIsLoadingMore(false);
    } catch (err) {
      setIsLoadingMore(false);
      setError(
        err instanceof Error ? err.message : "Failed to fetch more reminders"
      );
    }
  }, [memberId, category, searchTerm, page, isLoadingMore, hasMore]);

  return {
    reminders,
    loading,
    error,
    category,
    setCategory,
    hasReminders,
    searchTerm,
    setSearchTerm,
    page,
    hasMore,
    isLoadingMore,
    totalCount,
    checkRemindersPresent,
    fetchReminders,
    fetchMoreReminders,
    getReminder,
    addReminder,
    updateReminder,
    deleteReminder,
  };
};
